import React, { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { deleteRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import CheckboxTree from 'react-checkbox-tree'
import 'react-checkbox-tree/lib/react-checkbox-tree.css'
import { KTSVG } from '../../_metronic/helpers'
import ChartOfAccountCreate from '../components/ChartOfAccountCreate';
import ChartOfAccountEdit from '../components/ChartOfAccountEdit';
import { Link } from 'react-router-dom';

// Define the shape of your nodes if you have a specific structure
interface Node {
  value: string | number
  label: string
  children?: Node[]
  icon?: string | React.ReactNode // Assuming the icon is just the class name string from the backend
  ledger_type?: string
}


function ManageChartOfAccount() {

  const [checked] = useState<string[]>([])
  const [expanded, setExpanded] = useState<string[]>(['Assets'])
  const [ledgers, setLedgers] = useState<Node[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filterText, setFilterText] = useState('');
  const [filteredNodes, setFilteredNodes] = useState<Node[]>([]); // Cast to Node[] if necessary

  // Additional state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [idAccLedgers, setIdAccLedgers] = useState<number | null>(null); // Cast to Node[] if necessary

  type BasicObject = {
    [key: string]: string | React.ReactNode;
  };

  const icons: BasicObject = {
    check: '',
    uncheck: '',
    halfCheck: <span className="rct-icon rct-icon-half-check" />,
    expandClose: <i className='fa fa-chevron-right'></i>,
    expandOpen: <i className='fa fa-chevron-down'></i>,
    expandAll: <span className="rct-icon rct-icon-expand-all"> Expand All</span>,
    collapseAll: <span className="rct-icon rct-icon-collapse-all"> Collapse All</span>,
    parentClose: <span className="rct-icon rct-icon-parent-close" />,
    parentOpen: <span className="rct-icon rct-icon-parent-open" />,
    leaf: <i className='fa fa-file'></i>,
    group: <i className='fa fa-folder text-warning'></i>,
    gl: <i className='fa fa-file text-success'></i>,
  };


  const renderTreeNodes = (nodes: Node[]): Node[] => {
    return nodes.map(node => ({
      ...node,
      icon: node.ledger_type && icons[node.ledger_type] ? icons[node.ledger_type] : <i className='fa fa-folder' />, // default to folder icon if none provided
      children: node.children ? renderTreeNodes(node.children) : undefined,
    }));
  };

  async function getChartOfAccounts() {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + 'chart-of-accounts',
        getRequestOptions()
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const json = await response.json();
      if (json?.status === false) {
        throw new Error(json?.errorMessage ?? 'Failed to load chart of accounts.');
      }

      const nodesFromApi: Node[] = Array.isArray(json?.data) ? (json.data as Node[]) : [];
      const withIcons = renderTreeNodes(nodesFromApi);

      setLedgers(withIcons);
      setFilteredNodes(withIcons);

      if (!withIcons.length) {
        setLoadError('No chart of accounts found.');
      }
    } catch (error: any) {
      console.error('Failed to load chart of accounts', error);
      setLedgers([]);
      setFilteredNodes([]);
      setLoadError(error?.message ?? 'Unable to load chart of accounts.');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getChartOfAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onExpand = (expanded: string[]) => {
    setExpanded(expanded)
  }

  const findNodeByValue = (nodes: Node[], target: string | number): Node | null => {
    for (const node of nodes) {
      if (String(node.value) === String(target)) {
        return node;
      }
      if (node.children) {
        const childMatch = findNodeByValue(node.children, target);
        if (childMatch) {
          return childMatch;
        }
      }
    }
    return null;
  };

  const onClick = (value: any) => {
    const node = findNodeByValue(ledgers, value.value);
    setSelectedNode(node);

    const numericId = Number(node?.value ?? value.value);
    const hasValidId = !Number.isNaN(numericId);

    setIsEditing(hasValidId);
    setIdAccLedgers(hasValidId ? numericId : null);
  };

  const [createLedgerModalShow, setCreateLedgerModalShow] = useState(false);

  const handleCreateLedgerModalShow = () => {
    setCreateLedgerModalShow(true);
  }

  const handleCreateLedgerModalClose = () => {
    setCreateLedgerModalShow(false);
    resetSelection();
    getChartOfAccounts();
  }

  const [editLedgerModalShow, setEditLedgerModalShow] = useState(false);

  const handleEditLedgerModalShow = () => {
    setEditLedgerModalShow(true);
  }

  const handleEditLedgerModalClose = () => {
    setEditLedgerModalShow(false);
    resetSelection();
    getChartOfAccounts();
  }

  const resetSelection = () => {
    setSelectedNode(null);
    setIsEditing(false);
    setIdAccLedgers(null);
  }


  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const filter = value.trim();
    setFilterText(filter);
    filterTree(filter);
  };
  
  const filterTree = (filterText: string) => {
    if (!filterText) {
      setFilteredNodes(ledgers);
      return;
    }
    const nodesFiltered = ledgers.reduce<Node[]>((filtered, node) => filterNodes(filtered, node, filterText), []);
    setFilteredNodes(nodesFiltered);
  };
  
  const filterNodes = (filtered: Node[], node: Node, filterText: string) => {

    const children = (node.children || []).reduce<Node[]>((acc, child) => filterNodes(acc, child, filterText), []);
  
    if (
      node.label.toLowerCase().includes(filterText.toLowerCase()) ||
      children.length
    ) {
      // filtered.push({ ...node, children });
      filtered.push({ ...node, ...children.length && { children } });
    }
  
    return filtered;
  };

  const canEdit = isEditing && idAccLedgers !== null;
  const canDelete = useMemo(() => {
    if (!selectedNode || idAccLedgers === null) {
      return false;
    }
    if (selectedNode.ledger_type !== 'gl') {
      return false;
    }
    if (selectedNode.children && selectedNode.children.length > 0) {
      return false;
    }
    return true;
  }, [selectedNode, idAccLedgers]);

  const handleDeleteLedger = () => {
    if (!selectedNode || idAccLedgers === null) {
      return;
    }

    const ledgerLabel = selectedNode.label ?? 'this ledger';

    Swal.fire({
      title: 'Are you sure?',
      text: `You will not be able to recover ${ledgerLabel}!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
      showLoaderOnConfirm: true,
      customClass: {
        confirmButton: 'btn btn-danger me-2',
        cancelButton: 'btn btn-secondary'
      },
      preConfirm: async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}chart-of-accounts/${idAccLedgers}`,
            deleteRequestOptions()
          );

          const data = await response.json();

          if (!response.ok || !data.success) {
            const message =
              Array.isArray(data?.errors) && data.errors.length > 0
                ? data.errors.join('<br>')
                : data?.errorMessage || `Request failed with status ${response.status}`;
            Swal.showValidationMessage(message);
            return null;
          }

          return data;
        } catch (error: any) {
          const message = error?.message ?? 'Request failed';
          Swal.showValidationMessage(message);
          return null;
        }
      }
    }).then((result) => {
      if (result.isConfirmed) {
        if (result.value?.success) {
          Swal.fire(
            'Deleted!',
            `${ledgerLabel} has been deleted.`,
            'success'
          );
          resetSelection();
          setFilterText('');
          getChartOfAccounts();
        } else {
          const message =
            Array.isArray(result.value?.errors) && result.value.errors.length > 0
              ? result.value.errors.join('<br>')
              : result.value?.errorMessage || 'Unable to delete ledger.';

          Swal.fire(
            'Error!',
            message,
            'error'
          );
        }
      }
    });
  };

  return (
    <>
      <div className='card card-custom'>
        <div className='card-header'>
          <div className='card-title'>
            <KTSVG
              path='/media/svg/icons/Text/Bullet-list.svg'
              className='svg-icon-1 svg-icon-primary'
            />
            <span className='text fw-bolder' style={{ marginLeft: '10px' }}>
              Manage Chart Of Account
            </span>
          </div>
          <div className='card-toolbar'>
            <div className='d-flex justify-content-end'>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => handleCreateLedgerModalShow()}><i className="fa fa-plus"></i>Add Ledger</button>
            </div>
          </div>

        </div>

        <div className='card-body'>
          <div className="row mb-2">
            <div className="col-md-3 col-sm-4"><i className="fa fa-folder text-warning"></i> Group </div>
            <div className="col-md-3 col-sm-4"><i className="fa fa-file text-success"></i> General Ledger </div>
            
            <div className="col-md-3 col-sm-4">
              {canEdit && (
                <button className='btn btn-sm btn-light-primary me-2' onClick={() => handleEditLedgerModalShow()}>
                  <i className="fa fa-edit text-success"></i> Edit
                </button>
                )}
              {canDelete && (
                <button className='btn btn-sm btn-light-danger me-2' onClick={handleDeleteLedger}>
                  <i className="fa fa-trash"></i> Delete
                </button>
              )}
            </div>
           
            
          </div>
          <div className="row">
            <div className="col-md-12">
                <div className='bg-warning p-2 mb-3'>
                  <p className='p-0 m-0'>To create a general ledger under Accounts receivable please go to <Link to={`/setup-company-list`} className="text-primary"> Client management page.</Link></p>
                  <p className='p-0 m-0'>To create a general ledger under Accounts payable please go to <Link to={`/vendors`} className="text-primary"> vendor management page.</Link></p>
                  <p className='p-0 m-0'>To create a general ledger under Cash at Hand & Cash at Bank please go to <Link to={`/bank-accounts-cash`} className="text-primary"> Bank accounts & Cash Box page.</Link></p>
                  <p className='p-0 m-0'>Ledger will show up in trial balance and chart of accounts when a <Link to={`/po-create`} className="text-primary"> Purchase</Link> or <Link to={`/sales-order-create`} className="text-primary">Sales order</Link> is created.</p>
                  <p className='p-0 m-0'>To see the automatic voucher setup <a href="https://docs.google.com/spreadsheets/d/19fuQ3l1e1SOT20b9-HiYEYn8z1JXuEm5jagQJguYSUw/edit?pli=1#gid=0" className="text-primary" target='blank'> click here </a>.</p>
                </div>
            </div>
          </div>
          <div className="row">
            <div className="col-6">

              <div className="filter-container">
                <div className='form-group mb-2 col-6'>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search..."
                    type="text"
                    value={filterText}
                    onChange={onFilterChange}
                  />
                </div>
                

                {isLoading ? (
                  <div className='text-center py-10'>
                    <div className='spinner-border text-primary mb-3' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <div className='fw-semibold text-muted'>Loading chart of accounts...</div>
                  </div>
                ) : loadError ? (
                  <div className='text-center py-10'>
                    <div className='fw-semibold text-danger'>{loadError}</div>
                    <button className='btn btn-sm btn-light-primary mt-3' onClick={getChartOfAccounts}>
                      Retry
                    </button>
                  </div>
                ) : filteredNodes.length === 0 ? (
                  <div className='text-center py-10 text-muted fw-semibold'>
                    No chart of accounts data available.
                  </div>
                ) : (
                  <CheckboxTree
                    checked={checked}
                    expandOnClick
                    expanded={expanded}
                    nodes={filteredNodes as any} // if your Node interface does not match exactly with what CheckboxTree expects, you might need to assert it as any
                    showExpandAll
                    onClick={onClick}
                    onExpand={onExpand}
                    icons={icons}
                  />
                )}
              </div>


            </div>
          </div>
          <ChartOfAccountCreate show={createLedgerModalShow} handleClose={handleCreateLedgerModalClose} id={Date.now()} />
          {canEdit && idAccLedgers !== null && (
            <ChartOfAccountEdit
              show={editLedgerModalShow}
              handleClose={handleEditLedgerModalClose}
              id={idAccLedgers}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default ManageChartOfAccount
